/**
 * ThemedTabs Component
 * 
 * Tabs component that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedTabsProps, ThemedTabItem } from '../../types/immersive-theme.types';
import {
  neumorphismTabs,
  neumorphismTab,
  neumorphismTabActive,
} from './theme-styles/neumorphism';
import {
  brutalistTabs,
  brutalistTab,
  brutalistTabActive,
  brutalistTabHover,
} from './theme-styles/brutalist';
import {
  retroTerminalTabs,
  retroTerminalTab,
  retroTerminalTabActive,
  retroTerminalTabHover,
} from './theme-styles/retro-terminal';
import {
  organicNatureTabs,
  organicNatureTab,
  organicNatureTabActive,
  organicNatureTabHover,
} from './theme-styles/organic-nature';
import {
  luxuryGoldTabs,
  luxuryGoldTab,
  luxuryGoldTabActive,
  luxuryGoldTabHover,
} from './theme-styles/luxury-gold';

// Tab list container styles per theme
const tabListStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    display: 'flex',
    borderBottom: '1px solid #e5e7eb',
    gap: '0',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTabs,
  [ImmersiveThemeType.BRUTALIST]: brutalistTabs,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTabs,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTabs,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTabs,
};

// Tab button styles per theme
const tabStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    padding: '12px 20px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    transition: 'all 0.15s ease',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTab,
  [ImmersiveThemeType.BRUTALIST]: brutalistTab,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTab,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTab,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTab,
};

// Active tab styles per theme
const activeTabStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTabActive,
  [ImmersiveThemeType.BRUTALIST]: brutalistTabActive,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTabActive,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTabActive,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTabActive,
};

// Hover tab styles per theme
const hoverTabStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { color: '#374151' },
  [ImmersiveThemeType.NEUMORPHISM]: { color: 'var(--neu-primary)' },
  [ImmersiveThemeType.BRUTALIST]: brutalistTabHover,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTabHover,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTabHover,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTabHover,
};

// Size configurations
const sizeConfig: Record<string, React.CSSProperties> = {
  sm: { padding: '8px 14px', fontSize: '13px' },
  md: { padding: '12px 20px', fontSize: '14px' },
  lg: { padding: '16px 28px', fontSize: '16px' },
};

// Variant configurations
const getVariantStyles = (theme: ImmersiveThemeType, variant: string): { list: React.CSSProperties; tab: React.CSSProperties; active: React.CSSProperties } => {
  const variants: Record<string, { list: React.CSSProperties; tab: React.CSSProperties; active: React.CSSProperties }> = {
    line: {
      list: { borderBottom: theme === ImmersiveThemeType.BRUTALIST ? 'var(--brut-border)' : '1px solid rgba(0, 0, 0, 0.1)' },
      tab: { borderBottom: '2px solid transparent', marginBottom: '-1px' },
      active: { borderBottomColor: 'currentColor' },
    },
    enclosed: {
      list: { gap: '-1px' },
      tab: { 
        border: '1px solid transparent', 
        borderBottom: 'none',
        borderRadius: theme === ImmersiveThemeType.BRUTALIST ? '0' : '8px 8px 0 0',
      },
      active: { 
        borderColor: 'rgba(0, 0, 0, 0.1)', 
        background: theme === ImmersiveThemeType.RETRO_TERMINAL ? 'var(--term-bg-secondary)' : '#ffffff',
      },
    },
    pills: {
      list: { gap: '8px', border: 'none' },
      tab: { 
        borderRadius: theme === ImmersiveThemeType.BRUTALIST ? '0' : '999px',
        border: 'none',
      },
      active: { 
        background: theme === ImmersiveThemeType.RETRO_TERMINAL 
          ? 'var(--term-text)' 
          : theme === ImmersiveThemeType.LUXURY_GOLD
            ? 'var(--lux-gold)'
            : '#3b82f6',
        color: theme === ImmersiveThemeType.RETRO_TERMINAL 
          ? 'var(--term-bg)' 
          : theme === ImmersiveThemeType.LUXURY_GOLD
            ? 'var(--lux-bg)'
            : '#ffffff',
      },
    },
  };

  return variants[variant] || variants.line;
};

export const ThemedTabs: React.FC<ThemedTabsProps> = ({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  variant = 'line',
  size = 'md',
  fullWidth = false,
  centered = false,
  className,
}) => {
  const { theme, reducedMotion } = useImmersiveTheme();
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Use controlled or internal state
  const activeTab = controlledActiveTab ?? internalActiveTab;

  // Update internal state when defaultTab changes
  useEffect(() => {
    if (defaultTab && !controlledActiveTab) {
      setInternalActiveTab(defaultTab);
    }
  }, [defaultTab, controlledActiveTab]);

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    if (!controlledActiveTab) {
      setInternalActiveTab(tabId);
    }
    onChange?.(tabId);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    const enabledTabs = tabs.filter(t => !t.disabled);
    const currentEnabledIndex = enabledTabs.findIndex(t => t.id === tabs[currentIndex].id);

    let nextIndex: number | null = null;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        nextIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledTabs.length - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        nextIndex = currentEnabledIndex < enabledTabs.length - 1 ? currentEnabledIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = enabledTabs.length - 1;
        break;
    }

    if (nextIndex !== null) {
      const nextTab = enabledTabs[nextIndex];
      tabRefs.current.get(nextTab.id)?.focus();
      handleTabChange(nextTab.id);
    }
  };

  // Get variant-specific styles
  const variantStyles = getVariantStyles(theme, variant);

  // Compute tab list styles
  const computedListStyles: React.CSSProperties = {
    ...tabListStyles[theme],
    ...variantStyles.list,
    ...(fullWidth ? { width: '100%' } : {}),
    ...(centered ? { justifyContent: 'center' } : {}),
    ...(reducedMotion ? { transition: 'none' } : {}),
  };

  // Compute individual tab styles
  const computedTabStyles = (tab: ThemedTabItem, isActive: boolean, isHovered: boolean): React.CSSProperties => ({
    ...tabStyles[theme],
    ...sizeConfig[size],
    ...variantStyles.tab,
    ...(isActive ? { ...activeTabStyles[theme], ...variantStyles.active } : {}),
    ...(isHovered && !isActive ? hoverTabStyles[theme] : {}),
    ...(tab.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
    ...(fullWidth ? { flex: 1 } : {}),
    ...(reducedMotion ? { transition: 'none' } : {}),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    outline: 'none',
  });

  // Badge styles
  const badgeStyles: React.CSSProperties = {
    background: theme === ImmersiveThemeType.RETRO_TERMINAL 
      ? 'var(--term-text)' 
      : theme === ImmersiveThemeType.LUXURY_GOLD
        ? 'var(--lux-gold-solid)'
        : '#ef4444',
    color: theme === ImmersiveThemeType.RETRO_TERMINAL 
      ? 'var(--term-bg)' 
      : theme === ImmersiveThemeType.LUXURY_GOLD
        ? 'var(--lux-bg)'
        : '#ffffff',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: theme === ImmersiveThemeType.BRUTALIST ? '0' : '999px',
    minWidth: '18px',
    textAlign: 'center',
  };

  // Find active tab content
  const activeTabContent = tabs.find(t => t.id === activeTab)?.content;

  return (
    <div className={className}>
      {/* Tab list */}
      <div role="tablist" style={computedListStyles}>
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={(el) => el && tabRefs.current.set(tab.id, el)}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            aria-disabled={tab.disabled}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => !tab.disabled && handleTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onMouseEnter={() => !tab.disabled && setHoveredTab(tab.id)}
            onMouseLeave={() => setHoveredTab(null)}
            style={computedTabStyles(tab, activeTab === tab.id, hoveredTab === tab.id)}
            disabled={tab.disabled}
          >
            {/* Icon */}
            {tab.icon && <span style={{ display: 'flex', alignItems: 'center' }}>{tab.icon}</span>}
            
            {/* Label with terminal prefix */}
            <span>
              {theme === ImmersiveThemeType.RETRO_TERMINAL && activeTab === tab.id && '> '}
              {tab.label}
            </span>
            
            {/* Badge */}
            {tab.badge !== undefined && (
              <span style={badgeStyles}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`tabpanel-${tab.id}`}
          aria-labelledby={`tab-${tab.id}`}
          hidden={activeTab !== tab.id}
          tabIndex={0}
          style={{
            padding: '20px 0',
            ...(activeTab !== tab.id ? { display: 'none' } : {}),
          }}
        >
          {activeTab === tab.id && tab.content}
        </div>
      ))}
    </div>
  );
};

export default ThemedTabs;
